import { Button, Input, useToast } from '@/components';
import { apiCall } from '@/lib/api';
import { createMutation, createQuery, useQueryClient } from '@tanstack/solid-query';
import {
  ApiClientError,
  type ReleaseEnvironment,
  listRequiredArtifacts,
  putRequiredArtifacts,
} from '@uchat-team-management/shared';
import { Show, createSignal } from 'solid-js';

/** Comma-separated "platform/arch" pairs, e.g. "windows/x86_64, darwin/aarch64". */
export const parseRequiredArtifacts = (text: string): { platform: string; arch: string }[] =>
  text
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .map((pair) => {
      const [platform = '', arch = ''] = pair.split('/').map((p) => p.trim());
      return { platform, arch };
    })
    .filter((p) => p.platform && p.arch);

export const formatRequiredArtifacts = (
  items: readonly { platform: string; arch: string }[],
): string => items.map((i) => `${i.platform}/${i.arch}`).join(', ');

export const RequiredArtifactsEditor = (props: {
  productId: string;
  environment: ReleaseEnvironment;
  canManage: boolean;
}) => {
  const toast = useToast();
  const qc = useQueryClient();
  const query = createQuery(() => ({
    queryKey: ['required-artifacts', props.productId, props.environment] as const,
    queryFn: () =>
      apiCall(listRequiredArtifacts, {
        params: { productId: props.productId },
        query: { environment: props.environment },
      }),
  }));
  const save = createMutation(() => ({
    mutationFn: (items: { platform: string; arch: string }[]) =>
      apiCall(putRequiredArtifacts, {
        params: { productId: props.productId },
        query: { environment: props.environment },
        body: { items },
      }),
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: ['required-artifacts', props.productId, props.environment],
      }),
  }));

  // Dirty draft wins over server data until this field's own save succeeds; non-dirty
  // fields re-sync from refetches instead of latching a one-shot snapshot (UCH-295).
  const serverText = () => (query.data ? formatRequiredArtifacts(query.data.data) : '');
  const [draft, setDraft] = createSignal<string | null>(null);
  const text = () => draft() ?? serverText();

  const onSave = async () => {
    try {
      await save.mutateAsync(parseRequiredArtifacts(text()));
      setDraft(null);
      toast.success('Required artifacts saved');
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.error.message : 'Save failed');
    }
  };

  return (
    <div class="mt-1 flex items-end gap-2">
      <div class="flex-1">
        <Input
          label="Required artifacts (platform/arch, comma-separated)"
          value={text()}
          disabled={!props.canManage}
          onInput={(e) => setDraft(e.currentTarget.value)}
          placeholder="windows/x86_64, darwin/aarch64"
          aria-label={`${props.environment} required artifacts`}
        />
      </div>
      <Show when={props.canManage}>
        <Button
          variant="secondary"
          onClick={onSave}
          loading={save.isPending}
          disabled={query.isPending}
        >
          Save
        </Button>
      </Show>
    </div>
  );
};
